import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import API_ROUTES from "~/api-routes";
import apiService from "~/services/service";
import { z } from "zod";
import useFileUpload from "~/utils/useFileUpload";
import { getPeriodFromFileYearMonth } from "~/utils/fileUtils";
import { useSelector } from "react-redux";
import useAttachmentFilter from "~/hooks/useAttachmentFilter";
import {
  buildCategoryFoldersFromDocuments,
  getDocumentCategoryCode,
} from "~/utils/categoryFolders";

const useOfficerAttachment = () => {
  const [filesData, setFilesdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(true);
  const [entityData, setEntityData] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);
  const [attachmentConfirmModal, setAttachmentConfirmModal] = useState(false);
  const [temporaryMessage, setTemporaryMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<Record<string, any>>({});
  const [restoreMessage, setRstoreMessage] = useState("");
  const [isAttachmentLoader, setIsAttachmentLoader] = useState(false);
  const [first, setFirst] = useState(0);
  const { id } = useParams();
  const { uploadFile, updateTag, getFileVersion, uuidToUlid } = useFileUpload();
  const [filterData, setFilterData] = useState({});
  const [categoryFolders, setCategoryFolders] = useState<
    { id: string; code: string; name: string; isCategoryFolder?: boolean }[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    code: string;
    name: string;
    isCategoryFolder?: boolean;
  } | null>(null);
  const [chipdata, setChipData] = useState({});
  const removeFnRef = useRef(null);
  const {
    data,
    loading: filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    onPageChange: filterPageChange,
    currentPage,
  } = useAttachmentFilter();
  const { id: userId } = useSelector((state) => state.login);
  const fileSupported = [
    "xls",
    "csv",
    "xml",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "txt",
    "doc",
    "docx",
    "xlsx",
  ];
  const fetchOfficerData = async () => {
    setLoading(true);
    const { data } = await apiService(API_ROUTES.FETCH_OFFICER, "GET", {
      replace: { id },
    });
    setEntityData(data);
  };
  const fetchOfficerAttachments = async (skipCache = false) => {
    const { data } = await apiService(API_ROUTES.FETCH_DOCUMENTS, "GET", {
      replace: { id, type: "OFFICER" },
      skipCache,
    });
    if (data?.length) {
      setIsDataAvailable(true);
      setFilesdata(data);
      setCategoryFolders(buildCategoryFoldersFromDocuments(data));
    } else {
      setIsDataAvailable(false);
      setCategoryFolders([]);
    }
    setLoading(false);
  };
  useEffect(() => {
    fetchOfficerData();
    fetchOfficerAttachments();
  }, []);
  const handleChangePage = (e: {
    page: number;
    rows: number;
    first: number;
  }) => {
    setPage(e.page);
    setRows(e.rows);
    setFirst(e.first);
  };
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const uploadFileHandler = async (files) => {
    setIsFileUploaded(false);
    try {
      setLoading(true);
      const formData = new FormData();
      const fileOutput = [];
      const attachments = [];
      let tags, metadata;
      for (let i = 0; i < files.length; i++) {
        const file = files[i]?.file;
        formData.append("file", file);
        let fileData = {
          fileName: file?.fileName,
          category: file?.category,
          subCategory: file?.subcategory,
        };
        if (file?.description) {
          fileData.description = file?.description;
        }
        fileOutput.push(fileData);
        formData.append("filesData", JSON.stringify(fileOutput));

        tags = {
          entity_id: uuidToUlid(entityData?.entity),
          country: entityData?.entityData?.country,
          temporary_file: "false",
          officer: uuidToUlid(id),
          category: file?.category,
          subCategory: file?.subcategory,
          year: new Date().getFullYear().toString(),
          ...(getPeriodFromFileYearMonth(file?.fileYearMonth) && {
            period: getPeriodFromFileYearMonth(file.fileYearMonth),
          }),
        };
        const version = `${await getFileVersion(tags)}`;
        metadata = {
          original_file_name: encodeURIComponent(file.name),
          file_size: file.size.toString(),
          file_type: file.type,
          user: userId,
          file_name: file.fileName,
          version,
          created_at: new Date().toISOString(),
          file_object_type: "OFFICER",
          ...(file?.description && {
            description: encodeURIComponent(file?.description),
          }),
        };

        const uploadData = await uploadFile(file, file.fileName, {
          entityId: entityData?.entity,
          serviceLineCode: "ENTITIES_PLUS",
          fileName: file.name,
        });
        metadata = { ...metadata, url: uploadData.url };
        attachments.push({
          url: uploadData.url,
          fileName: uploadData.id,
          name: file.fileName,
        });
        await updateTag(uploadData.id, tags, metadata);
        await apiService(API_ROUTES.ADD_DOCUMENT_AUDIT, "POST", {
          body: { attachments },
          replace: {
            id,
            type: "OFFICER",
          },
          schema: z.object({
            id: z.string().uuid(),
            type: z.enum(["OFFICER"]),
          }),
        });
      }
    } catch (e) {
      console.error(e);
      setIsFileUploaded(false);
    } finally {
      // Introduced a delay to allow time for the virus scan to complete after file upload,
      // ensuring the document list fetch reflects the latest and clean files.
      setTimeout(() => {
        setSuccessMessage("File uploaded successfully");
        setIsFileUploaded(true);
        setOpenDialog(false);
        fetchOfficerAttachments(true); // skipCache after upload
      }, 2000);
    }
  };

  const getType = () => {
    const name = entityData?.preferred_name
      ? `${entityData?.preferred_name}`
      : `${entityData?.name}${entityData?.last_name ? " " + entityData?.last_name : ""}`;
    return { name };
  };

  const handleDeleteDocument = (file) => {
    setAttachmentConfirmModal(true);
    setSelectedFile(file);
  };
  const selectedItems = Object.values(filterData || {}).filter(
    (value) =>
      !["category", "service_line", "entities", "year"].includes(value?.id),
  );
  const handleConfirmDelete = async () => {
    setIsAttachmentLoader(true);
    setRstoreMessage("");
    setTemporaryMessage("");
    const { status } = await apiService(
      API_ROUTES.DELETE_ATTACHMENT,
      "DELETE",
      {
        body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
        replace: { relatedId: id, relatedType: "OFFICER" },
      },
    );
    if (status) {
      setTimeout(async () => {
        setLoading(true);
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchOfficerAttachments(true); // skipCache after delete
        }
        setAttachmentConfirmModal(false);
        setTemporaryMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been deleted.`,
        );
        setIsAttachmentLoader(false);
      }, 2000);
    }
  };
  const handleRestoreAttachment = async () => {
    setLoading(true);
    setTemporaryMessage("");

    const { status } = await apiService(API_ROUTES.RESTORE_ATTACHMENT, "POST", {
      body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
    });
    if (status) {
      setTimeout(async () => {
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchOfficerAttachments(true); // skipCache after restore
        }
        setRstoreMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been restored.`,
        );
      }, 2000);
    }
  };

  const handleRemoveFilter = (id) => {
    if (removeFnRef.current) {
      const { value: updatedValue, grouped } = removeFnRef.current(id);
      setFilterData(updatedValue);
      handleFilterChange(grouped);
    }
  };
  const handleFilterChange = (data) => {
    const categories = Object.entries(data?.category || {}).map(
      ([key, value]) => ({
        category: key,
        subCategories: value?.map((sub) =>
          sub.includes("/") ? sub.split("/").pop() : sub,
        ),
      }),
    );
    setChipData(categories);
    onFilterChange({
      categories,
      typeId: id,
      type: "OFFICER",
    });
  };

  useEffect(() => {
    if (selectedItems?.length) {
      onFilterApply();
    } else {
      fetchOfficerAttachments();
    }
  }, [filterData]);

  const handleCategoryFolderClick = (folder: {
    id: string;
    code: string;
    name: string;
  }) => {
    setSelectedCategory(folder);
  };

  const displayFiles = selectedCategory
    ? filesData.filter(
        (f) => getDocumentCategoryCode(f) === selectedCategory.code,
      )
    : filesData;

  return {
    filesData,
    loading,
    page,
    rows,
    totalRecords,
    onPageChange: handleChangePage,
    isDataAvailable,
    handleOpenDialog,
    setOpenDialog,
    openDialog,
    uploadFileHandler,
    entityData,
    fileSupported,
    isFileUploaded,
    setIsFileUploaded,
    successMessage,
    setSuccessMessage,
    isAuditSidebarVisible,
    setAuditSidebarVisible,
    first,
    getType,
    handleDeleteDocument,
    attachmentConfirmModal,
    setAttachmentConfirmModal,
    handleConfirmDelete,
    temporaryMessage,
    isAttachmentLoader,
    handleRestoreAttachment,
    restoreMessage,
    setRstoreMessage,
    setTemporaryMessage,
    selectedItems,
    filterData,
    setFilterData,
    chipdata,
    setChipData,
    removeFnRef,
    handleRemoveFilter,
    handleFilterChange,
    data,
    filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    filterPageChange,
    currentPage,
    fetchOfficerAttachments,
    categoryFolders,
    selectedCategory,
    setSelectedCategory,
    handleCategoryFolderClick,
    displayFiles,
  };
};

export default useOfficerAttachment;
